import { ErrorCodes } from './ErrorCodes';
/**
 * A Class that contains contextual information about a particular error occurrence.
 *
 * We follow a Hexa-Decimal Error System.
 * [Int] has a size of 32-bit.
 * - The least Significant 16-bits(Bits 0-15) indicate a Component Error Code.
 *
 * - The most Significant 16-bits (Bits 16-31) indicate the Categories that the Error fall into.
 *   - We can add multiple categories for a single [Error]
 *     Example:
 *     ```
 *       val errorCode =
 *       PlatformErrorCodes.PLATFORM_SERVICE_ACCESS_TOKEN_FAILURE | //indicates Component Error
 *       PlatformErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK | // Adds Playback Category
 *       PlatformErrorCodes.ERROR_CATEGORY_MASK_NETWORK // Adds Network Category
 *     ```
 * see [{@link Error.componentErrorCode}] used to extract component level error code by removing all category Masks.
 * see [{@link Error.doesBelongToCategory}] used to check if the error belongs to a particular category.
 * see [{@link Error.hexErrorCode}] used to get the hexadecimal [String] representation of a particular error object.
 */
export interface Error {
  /**
   * Unique Error Code with all Category Masking Set.
   */
  errorCode: number;
  /**
   * Error message describing the Error.
   */
  errorMessage?: string;
  /**
   * The internal root-cause of the Error if any.
   */
  internalError?: Error;
  /**
   * The internal Contextual description of the Error if any.
   * This is usually set if the error is from a third-party source.
   */
  contextDescription?: string;
  /**
   * A Computed property that returns the component error code removing all CATEGORY MASK.
   */
  componentErrorCode?: number;
  /**
   * The hexadecimal [String] representation of a particular error object.
   */
  hexErrorCode?: string;
  /**
   * Checks if the error belongs to a particular category.
   */
  doesBelongToCategory: (category: number) => boolean;
}
export function createError(
  errorCode: number,
  errorMessage?: string,
  internalError?: Error,
  contextDescription?: string,
): Error {
  return {
    errorCode,
    errorMessage: errorMessage || 'Unknown error occured',
    internalError,
    contextDescription,
    componentErrorCode: getComponentErrorCode(errorCode),
    get hexErrorCode(): string {
      return getHexErrorCode(errorCode);
    },
    doesBelongToCategory: (category: number): boolean => {
      return (errorCode & category) === category;
    },
  };
}
export function isError(result: Error | any): result is Error {
  return (result as Error).errorCode !== undefined;
}
function getComponentErrorCode(errorCode: number): number {
  return errorCode & ~ErrorCodes.ERROR_CATEGORY_MASK_ALL;
}
export function getHexErrorCode(number: number): string {
  if (!number) {
    return '';
  }
  return number.toString(16).toUpperCase();
}
